import { useState } from "react";
import { OrderStatusBadge, PaymentStatusBadge } from "@/components/status-badges";
import { useOrders, useDeleteOrder } from "@/pages/order/hooks/useOrders";
import { PageLayout } from "@groot/shell/components/layout/PageLayout";
import { Button } from "@groot/ui/button";
import { Input } from "@groot/ui/input";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CreateOrderDialog } from "@/pages/order/components/CreateOrderDialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@groot/ui/table";
import { SkeletonTable } from "@groot/ui/loading-skeleton";
import { EmptyState } from "@groot/ui/empty-state";
import { RowActions, RowActionItem, RowActionSeparator } from "@/components/row-actions";
import { Search, ShoppingCart, Trash2, Eye, Plus } from "lucide-react";
import { useConfirm } from "@groot/ui/primitives";

export function Orders() {
  const { data: orders, isLoading } = useOrders();
  const deleteOrder = useDeleteOrder();
  const [search, setSearch] = useState("");
  const confirm = useConfirm();

  const filteredOrders = orders?.filter((o) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.orderNumber.toLowerCase().includes(q) ||
      o.customer?.name.toLowerCase().includes(q) ||
      (o.customer?.mobileNumber?.toLowerCase() ?? "").includes(q)
    );
  });

  const removeOrder = async (id: number) => {
    const confirmed = await confirm({
      title: "Delete order?",
      description:
        "This will permanently delete this order along with all its items, photo requests, and uploaded files. This action cannot be undone.",
      confirmLabel: "Delete Order",
      destructive: true,
    });
    if (!confirmed) return;
    try {
      await deleteOrder.mutateAsync(id);
      toast.success("Deleted", { description: "Order removed" });
    } catch (error) {
      console.error(error);
      toast.error("Error", { description: "Unable to delete order" });
    }
  };

  const actions = (
    <CreateOrderDialog
      trigger={
        <Button>
          <Plus className="size-4" />
          Create Order
        </Button>
      }
    />
  );

  return (
    <PageLayout title="Orders" description="Manage your orders" actions={actions}>
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Search by order #, customer name, or mobile..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        {isLoading && (
          <div className="overflow-hidden rounded-lg border">
            <SkeletonTable columns={7} rows={6} />
          </div>
        )}

        {!isLoading && filteredOrders && filteredOrders.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            {/* Mobile: one tappable row per order */}
            <ul className="md:hidden divide-y divide-border">
              {filteredOrders.map((order) => (
                <li key={order.id} className="flex items-start gap-2 py-3 pl-4 pr-2">
                  <Link to={`/orders/${order.id}`} className="min-w-0 flex-1 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-foreground">
                          {order.orderNumber}
                        </div>
                        <div className="truncate text-sm text-foreground">
                          {order.customer?.name}
                        </div>
                      </div>
                      <div className="shrink-0 text-sm font-semibold text-foreground">
                        ₹{order.total}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <OrderStatusBadge status={order.status} />
                      <PaymentStatusBadge status={order.paymentStatus} />
                      <span className="text-xs text-muted-foreground">
                        {new Date(order.orderDate).toLocaleDateString()}
                      </span>
                    </div>
                  </Link>
                  <OrderRowActions
                    orderId={order.id}
                    onDelete={removeOrder}
                    deleting={deleteOrder.isPending}
                  />
                </li>
              ))}
            </ul>

            <div className="hidden md:block overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Order #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="hidden lg:table-cell">Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden sm:table-cell">Payment</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredOrders.map((order) => (
                    <TableRow
                      key={order.id}
                      className="cursor-pointer hover:bg-muted/50 border-border"
                    >
                      <TableCell className="text-sm text-foreground">
                        <Link to={`/orders/${order.id}`} className="font-medium hover:text-primary">
                          {order.orderNumber}
                        </Link>
                      </TableCell>
                      <TableCell className="text-sm text-foreground whitespace-normal">
                        <div className="font-medium">{order.customer?.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {order.customer?.mobileNumber}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden lg:table-cell">
                        {new Date(order.orderDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <OrderStatusBadge status={order.status} />
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <PaymentStatusBadge status={order.paymentStatus} />
                      </TableCell>
                      <TableCell className="text-sm text-foreground text-right font-semibold">
                        ₹{order.total}
                      </TableCell>
                      <TableCell>
                        <OrderRowActions
                          orderId={order.id}
                          onDelete={removeOrder}
                          deleting={deleteOrder.isPending}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {!isLoading && filteredOrders?.length === 0 && (
          <EmptyState
            icon={ShoppingCart}
            title={search ? "No orders found" : "No orders yet"}
            description={
              search
                ? "Try adjusting your search terms"
                : "Get started by creating your first order"
            }
            action={
              !search && (
                <CreateOrderDialog
                  trigger={
                    <Button>
                      <Plus className="size-4" />
                      Create Order
                    </Button>
                  }
                />
              )
            }
          />
        )}
      </div>
    </PageLayout>
  );
}

function OrderRowActions({
  orderId,
  onDelete,
  deleting,
}: {
  orderId: number;
  onDelete: (id: number) => void;
  deleting: boolean;
}) {
  return (
    <RowActions>
      <RowActionItem asChild>
        <Link to={`/orders/${orderId}`}>
          <Eye className="size-4" />
          View
        </Link>
      </RowActionItem>
      <RowActionSeparator />
      <RowActionItem destructive onClick={() => onDelete(orderId)} disabled={deleting}>
        <Trash2 className="size-4" />
        Delete
      </RowActionItem>
    </RowActions>
  );
}
